import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Send, ArrowLeft, Loader2, Layers, Bot, Info, Paperclip, X } from "lucide-react";
import { Link, useLocation } from "wouter";
import { MultiModelResponseView, type ModelResponseData } from "@/components/MultiModelResponseView";
import { aiService, type OpenRouterModel as AIServiceModel } from "@/services/aiService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { creditsService, isUnlimitedAiCreditsEmail } from "@/services/creditsService";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { operatingLoopService } from "@/services/operatingLoopService";
import FileUploadButton from "@/components/FileUploadButton";
import { marked } from "marked";
import DOMPurify from "dompurify";

interface UserCredits {
  balance: string;
  totalEarned: string;
  totalSpent: string;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string | Array<{
    type: string;
    text?: string;
    image_url?: { url: string; detail?: string };
    input_audio?: { data: string };
    videoUrl?: { url: string };
    file?: { filename?: string; file_data?: string };
  }>;
  timestamp: Date;
  citations?: Array<{
    type: "url_citation";
    url_citation: {
      url: string;
      title?: string;
      content?: string;
      start_index?: number;
      end_index?: number;
    };
  }>;
  images?: Array<{ url: string; detail?: string }>;
}

// Configure marked for safe HTML output
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Helper function to render markdown as sanitized HTML
function renderMarkdownToHtml(content: string): string {
  try {
    const html = marked.parse(Array.isArray(content) ? content[0]?.text : content, { async: false }) as string;
    return DOMPurify.sanitize(html);
  } catch (error) {
    console.error("Error rendering markdown:", error);
    return DOMPurify.sanitize(content);
  }
}

// Helper function to determine content type from file type
const getContentType = (fileType: string): string => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('audio/')) return 'audio';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType === 'application/pdf') return 'pdf';
  return 'image'; // Default fallback
};

export default function MultiModelChat() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [maxModels, setMaxModels] = useState(2); // Dynamic state for max models
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [modelInfos, setModelInfos] = useState<Map<string, AIServiceModel>>(new Map());
  const [responses, setResponses] = useState<ModelResponseData[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: "user" | "assistant"; content: string; responses?: ModelResponseData[] }>
  >([]);
  const [fileAttached, setFileAttached] = useState<Array<{ type: string; data: string; filename?: string }>>([]);
  const [threadIds, setThreadIds] = useState<Map<string, string>>(new Map()); // Map model ID to thread ID
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: credits } = useQuery<UserCredits>({
    queryKey: ["/api/credits"],
    queryFn: async () => {
      const { creditsService } = await import("@/services/creditsService");
      return creditsService.getOrCreateUserCredits();
    },
  });

  const loginEmailForUnlimited = (
    user?.email?.trim() ||
    (user?.username?.includes("@") ? user.username.trim() : "")
  ).toLowerCase();
  const isUnlimitedAi = isUnlimitedAiCreditsEmail(loginEmailForUnlimited || null);
  const userBalance = parseInt(credits?.balance || "0");

  // Fetch available models from OpenRouter
  interface OpenRouterModel {
    id: string;
    name: string;
    description?: string;
    pricing?: {
      prompt?: string;
      completion?: string;
    };
    context_length?: number;
    architecture?: {
      modality?: string;
      tokenizer?: string;
      instruct_type?: string;
      output_modalities?: string[];
      input_modalities?: string[];
    };
    top_provider?: {
      name?: string;
      max_completion_tokens?: number;
    };
  }

  const { data: availableModels = [] } = useQuery<OpenRouterModel[]>({
    queryKey: ['/api/models'],
    queryFn: async () => {
      try {
        const models = await aiService.fetchModels();
        return models as OpenRouterModel[];
      } catch (error) {
        console.error("Failed to fetch models:", error);
        return [];
      }
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Get popular/default models for quick selection
  const popularModels = availableModels.length > 0
    ? availableModels
    : [];

  // Get model info for the first selected model (for file upload compatibility)
  const firstSelectedModelInfo = selectedModels.length > 0 
    ? modelInfos.get(selectedModels[0]) || availableModels.find(m => m.id === selectedModels[0])
    : null;

  // Create thread mutation (scoped by current workspace or personal)
  const createThreadMutation = useMutation({
    mutationFn: async (data: {
      messages: any[];
      modelId: string;
    }) => {
      return await chatThreadsService.createChatThread(
        {
          messages: data.messages,
          linkedSpaceIds: [],
          contextMode: "MAX",
          chatMode: "normal",
          title: `Multi-Model Chat - ${data.modelId.split('/').pop()}`,
        },
        currentWorkspaceId ?? null
      );
    },
    onSuccess: (thread, variables) => {
      // Store thread ID for this model
      setThreadIds((prev) => new Map(prev).set(variables.modelId, thread.id));
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
    },
  });

  // Update thread mutation (scoped by current workspace or personal)
  const updateThreadMutation = useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: Partial<{ messages: any[] }>;
    }) => {
      return await chatThreadsService.updateChatThread(id, data, currentWorkspaceId ?? null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
    },
  });

  const handleSend = async () => {
    if ((!input.trim() && fileAttached.length === 0) || isStreaming || selectedModels.length === 0) return;

    const gate = await creditsService.assertPositiveBalanceForAi();
    if (!gate.ok) {
      toast({
        title: "Credits depleted",
        description: "Purchase credits to continue using AI features.",
        variant: "destructive",
      });
      setLocation("/pricing");
      return;
    }

    const userMessageText = input.trim();
    setInput("");
    setIsStreaming(true);

    // Build user message with attachments
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: userMessageText || "",
      timestamp: new Date(),
    };

    // Build content array with text and all attachments
    if (fileAttached.length > 0) {
      const contentArray: any[] = [];
      
      if (userMessageText) {
        contentArray.push({
          type: 'text',
          text: userMessageText,
        });
      }

      // Add all attachments to content array
      for (const attachment of fileAttached) {
        if (attachment.type === 'image') {
          contentArray.push({
            type: 'image_url',
            image_url: {
              url: attachment.data
            }
          });
        } else if (attachment.type === 'audio') {
          contentArray.push({
            type: 'input_audio',
            input_audio: {
              data: attachment.data
            }
          });
        } else if (attachment.type === 'video') {
          contentArray.push({
            type: 'video_url',
            videoUrl: {
              url: attachment.data
            }
          });
        } else if (attachment.type === 'pdf') {
          contentArray.push({
            type: 'file',
            file: {
              filename: attachment?.filename,
              file_data: attachment?.data,
            },
          });
        }
      }

      userMessage.content = contentArray;
    }

    // Store user message in conversation history (text only for display)
    setConversationHistory((prev) => [
      ...prev,
      { 
        role: "user", 
        content: userMessageText || (fileAttached.length > 0 ? `[${fileAttached.length} file${fileAttached.length > 1 ? 's' : ''} attached]` : '') 
      },
    ]);

    // Initialize responses for each selected model
    const initialResponses: ModelResponseData[] = selectedModels.map((model) => {
      const modelInfo = modelInfos.get(model) || availableModels.find(m => m.id === model);
      return {
        model,
        modelDisplayName: modelInfo?.name || model.split("/")[1] || model,
        content: "",
        status: "pending",
      };
    });
    setResponses(initialResponses);

    // Clear file attachments after sending
    const attachmentsToClear = [...fileAttached];
    setFileAttached([]);

    try {
      // Build conversation history for each model
      const baseMessages = [
        ...conversationHistory
          .filter((msg) => msg.role === "user" || (msg.role === "assistant" && msg.content))
          .map((msg) => ({
            role: msg.role,
            content: msg.content,
          })),
      ];

      // Process each model in parallel
      const modelPromises = selectedModels.map(async (modelId) => {
        const modelInfo = modelInfos.get(modelId) || availableModels.find(m => m.id === modelId);
        const modelDisplayName = modelInfo?.name || modelId.split("/")[1] || modelId;
        
        // Get or create thread for this model
        let threadId = threadIds.get(modelId);
        const modelMessages = [...baseMessages, { role: "user" as const, content: userMessage.content }];
        
        let fullContent = '';
        let collectedCitations: Array<{
          type: "url_citation";
          url_citation: {
            url: string;
            title?: string;
            content?: string;
            start_index?: number;
            end_index?: number;
          };
        }> = [];
        let collectedImages: Array<{ url: string; detail?: string }> = [];

        try {
          const gated = await operatingLoopService.answerWithLoop({
            userId: user?.id,
            question: typeof userMessage.content === "string" ? userMessage.content : "",
            pickedModel: modelId,
            app: "compare_models",
            run: async ({ model, briefPrompt, outsideInfo }) => {
          for await (const chunk of aiService.streamChat({
            model,
            messages: [
              ...(briefPrompt ? [{ role: "system" as const, content: briefPrompt }] : []),
              ...modelMessages.map(m => ({ role: m.role, content: m.content })),
            ],
            modelInfo: modelInfo as AIServiceModel | undefined,
            stream: true,
            webSearchEnabled: outsideInfo,
            app: "compare_models",
          })) {
            if (chunk.type === "error") {
              throw new Error(chunk.error || "Unknown error");
            }

            if (chunk.type === "delta" && chunk.content) {
              fullContent += chunk.content;
              setResponses((prev) =>
                prev.map((r) =>
                  r.model === modelId
                    ? {
                        ...r,
                        content: fullContent,
                        status: "streaming",
                        citations: collectedCitations.length > 0 ? [...collectedCitations] : r.citations,
                      }
                    : r
                )
              );
            }

            if (chunk.type === "citations" && chunk.citations) {
              collectedCitations.push(...chunk.citations);
              setResponses((prev) =>
                prev.map((r) =>
                  r.model === modelId
                    ? {
                        ...r,
                        content: fullContent,
                        citations: [...collectedCitations],
                      }
                    : r
                )
              );
            }

            if (chunk.type === "images" && chunk.images) {
              collectedImages.push(...chunk.images);
              setResponses((prev) =>
                prev.map((r) =>
                  r.model === modelId
                    ? {
                        ...r,
                        content: fullContent,
                        citations: collectedCitations.length > 0 ? [...collectedCitations] : r.citations,
                        images: [...collectedImages],
                      }
                    : r
                )
              );
            }

            if (chunk.type === "done") {
              // Deduct credits for this model's response
              if (chunk.usage && modelInfo?.pricing) {
                const promptTokens = chunk.usage.prompt_tokens || 0;
                const completionTokens = chunk.usage.completion_tokens || 0;
                const promptPrice = modelInfo.pricing.prompt || "0";
                const completionPrice = modelInfo.pricing.completion || "0";

                try {
                  const { creditsDeducted, hadEnoughBalance } = await creditsService.deductCreditsForAIUsage({
                    promptTokens,
                    completionTokens,
                    promptPrice,
                    completionPrice,
                    model: modelId,
                    profitMultiplier: 3,
                  });

                  if (creditsDeducted > 0) {
                    if (hadEnoughBalance) {
                      toast({
                        title: "Credits Deducted",
                        description: `${creditsDeducted.toLocaleString()} credits used for ${modelDisplayName}.`,
                      });
                    } else {
                      toast({
                        title: "Insufficient Credits",
                        description: `Not enough credits for ${modelDisplayName}. ${creditsDeducted.toLocaleString()} needed; balance is now 0. Please purchase more credits.`,
                        variant: "destructive",
                      });
                    }
                  }

                  // Keep balances fresh across UI surfaces
                  queryClient.invalidateQueries({ queryKey: ["/api/credits"] });
                  queryClient.invalidateQueries({ queryKey: ["credits-overview"] });
                  queryClient.invalidateQueries({ queryKey: ["billing-subscription"] });
                } catch (creditError) {
                  console.error("Error deducting credits for multi-model chat:", creditError);
                  toast({
                    title: "Credit Processing Error",
                    description: "We could not deduct credits for this response. Please check your balance.",
                    variant: "destructive",
                  });
                }
              } else {
                console.warn("Credit deduction skipped: missing usage or pricing", {
                  hasUsage: !!chunk.usage,
                  hasPricing: !!modelInfo?.pricing,
                });
              }

              break;
            }
          }
          return { text: fullContent };
            },
          });
          fullContent = gated.text;

          // Mark as done
          setResponses((prev) =>
            prev.map((r) =>
              r.model === modelId
                ? {
                    ...r,
                    status: "done",
                  }
                : r
            )
          );

          // Save to thread
          const assistantMessage: Message = {
            id: `${Date.now()}-${modelId}`,
            role: "assistant",
            content: fullContent,
            timestamp: new Date(),
            citations: collectedCitations.length > 0 ? collectedCitations : undefined,
            images: collectedImages.length > 0 ? collectedImages : undefined,
          };

          const messagesToSave = [
            ...modelMessages.map((m: any) => ({
              id: typeof m === 'object' && m.id ? m.id : Date.now().toString() + Math.random(),
              role: m.role,
              content: m.content,
              timestamp: new Date().toISOString(),
            })),
            {
              id: assistantMessage.id,
              role: assistantMessage.role,
              content: assistantMessage.content,
              timestamp: assistantMessage.timestamp.toISOString(),
              citations: assistantMessage.citations,
              images: assistantMessage.images,
            },
          ];

          if (!threadId) {
            // Create new thread
            const newThread = await createThreadMutation.mutateAsync({
              messages: messagesToSave,
              modelId,
            });
            threadId = newThread.id;
          } else {
            // Update existing thread
            await updateThreadMutation.mutateAsync({
              id: threadId,
              data: { messages: messagesToSave },
            });
          }
        } catch (error: any) {
          console.error(`Error processing model ${modelId}:`, error);
          setResponses((prev) =>
            prev.map((r) =>
              r.model === modelId
                ? {
                    ...r,
                    status: "error",
                    error: error.message || "Failed to get response",
                  }
                : r
            )
          );
        }
      });

      // Wait for all models to complete
      await Promise.all(modelPromises);

      // Refresh credits after all models complete
      queryClient.invalidateQueries({ queryKey: ["/api/credits"] });

      // Update conversation history
      setConversationHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Multi-model comparison completed",
          responses: responses,
        },
      ]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        variant: "destructive",
      });
      setResponses((prev) =>
        prev.map((r) =>
          r.status === "pending" || r.status === "streaming"
            ? { ...r, status: "error", error: error.message }
            : r
        )
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const handleSelectResponse = (model: string, content: string) => {
    const modelInfo = modelInfos.get(model) || availableModels.find(m => m.id === model);
    toast({
      title: "Response selected",
      description: `Using response from ${modelInfo?.name || model}`,
    });
  };

  // Update model infos when models are selected
  useEffect(() => {
    selectedModels.forEach((modelId) => {
      if (!modelInfos.has(modelId)) {
        const model = availableModels.find(m => m.id === modelId);
        if (model) {
          setModelInfos((prev) => new Map(prev).set(modelId, model));
        }
      }
    });
  }, [selectedModels, availableModels]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [responses, conversationHistory]);

  const promptLength = input.length;

  // Handle model selection
  const handleModelToggle = (modelId: string) => {
    if (selectedModels.includes(modelId)) {
      setSelectedModels(selectedModels.filter(id => id !== modelId));
      // Remove thread ID for deselected model
      setThreadIds((prev) => {
        const newMap = new Map(prev);
        newMap.delete(modelId);
        return newMap;
      });
    } else if (selectedModels.length < maxModels) {
      setSelectedModels([...selectedModels, modelId]);
      const model = availableModels.find(m => m.id === modelId);
      if (model) {
        setModelInfos((prev) => new Map(prev).set(modelId, model));
      }
    } else {
      toast({
        title: "Maximum models reached",
        description: `You can only select up to ${maxModels} models at a time.`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="page-multi-model-chat">
      <div className="border-b px-4 py-3 flex items-center justify-between gap-4 flex-none">
        <div className="flex items-center gap-3">
          <Link href="/chat">
            <Button variant="ghost" size="icon" data-testid="button-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Compare AI Models</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" data-testid="badge-balance">
            Balance: {isUnlimitedAi ? "Unlimited" : `${userBalance} credits`}
          </Badge>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 p-4 gap-4">
        {/* Model Selector */}
        <div className="flex-none">
          <Card data-testid="card-model-selector">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Select Models to Compare
              </CardTitle>
             {/* <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-muted-foreground">Max models:</span>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={maxModels}
                  onChange={(e) => {
                    const newMax = parseInt(e.target.value) || 2;
                    if (newMax >= 1 && newMax <= 5) {
                      setMaxModels(newMax);
                      if (selectedModels.length > newMax) {
                        setSelectedModels(selectedModels.slice(0, newMax));
                      }
                    }
                  }}
                  className="w-16 h-7 px-2 text-xs border rounded"
                  data-testid="input-max-models"
                />
              </div>*/}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                {availableModels.length > 0 ? (
                  availableModels.map((model) => {
                    const isSelected = selectedModels.includes(model.id);
                    const canSelectMore = selectedModels.length < maxModels;
                    const isDisabled = !isSelected && !canSelectMore;

                    return (
                      <div
                        key={model.id}
                        data-testid={`model-option-${model.id.replace("/", "-")}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          isSelected
                            ? "bg-primary/5 border-primary/30"
                            : "hover:bg-muted/50 border-transparent bg-muted/20"
                        } ${isDisabled && !isSelected ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => !isDisabled && handleModelToggle(model.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {}}
                          className="mt-0.5"
                          data-testid={`checkbox-model-${model.id.replace("/", "-")}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium truncate">{model.name || model.id.split('/').pop()}</span>
                          </div>
                          {model.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {model.description}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-center py-4 text-sm text-muted-foreground">
                    Loading models...
                  </div>
                )}
              </div>

              {selectedModels.length > 0 && (
                <div className="pt-3 border-t flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {selectedModels.length} model{selectedModels.length > 1 ? "s" : ""} selected
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {selectedModels.length === 0 && (
          <Card className="flex-none">
            <CardContent className="py-8 text-center">
              <Bot className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">Select Models to Compare</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Choose up to {maxModels} AI model{maxModels > 1 ? "s" : ""} above to compare their responses side by side.
                Each model will respond to your prompt independently.
              </p>
            </CardContent>
          </Card>
        )}

        {responses.length > 0 && (
          <ScrollArea className="flex-1 min-h-0" ref={scrollRef}>
            <MultiModelResponseView
              responses={responses}
              onSelectResponse={handleSelectResponse}
              isStreaming={isStreaming}
            />
          </ScrollArea>
        )}

        {conversationHistory.length > 0 && responses.length === 0 && (
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-4">
              {conversationHistory.map((msg, i) => (
                <Card key={i} className={msg.role === "user" ? "ml-12" : "mr-12"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {msg.role === "user" ? "You" : "AI"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{msg.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Input Area with File Upload */}
        <div className="flex-none border rounded-lg bg-background">
          {/* Active indicators */}
          {fileAttached.length > 0 && (
            <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 flex-wrap">
              {fileAttached.map((file, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="text-xs gap-1 h-5 cursor-pointer hover:bg-destructive/20"
                  onClick={() => setFileAttached(fileAttached.filter((_, i) => i !== idx))}
                >
                  <Paperclip className="h-3 w-3" />
                  {file.filename || `File ${idx + 1}`}
                  <X className="h-3 w-3 ml-0.5" />
                </Badge>
              ))}
            </div>
          )}

          <div className="relative flex items-end gap-2 p-2">
            <Textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                // Auto-resize textarea
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, window.innerHeight * 0.3) + 'px';
              }}
              placeholder={
                selectedModels.length === 0
                  ? "Select models above first..."
                  : `Ask ${selectedModels.length} model${selectedModels.length > 1 ? "s" : ""} a question...`
              }
              className="min-h-[44px] max-h-[30vh] resize-none border-0 focus-visible:ring-0 py-2.5 px-3 pr-10 flex-1"
              style={{ height: '44px' }}
              disabled={isStreaming || selectedModels.length === 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              data-testid="input-prompt"
            />
            
            {/* Send button - positioned inside textarea */}
            <Button
              onClick={handleSend}
              disabled={(!input.trim() && fileAttached.length === 0) || isStreaming || selectedModels.length === 0}
              size="icon"
              className="h-8 w-8 absolute right-3 bottom-3"
              data-testid="button-send"
              aria-label="Send message"
            >
              {isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Action Bar */}
          <div className="flex items-center justify-between gap-1 px-2 pb-2">
            <div className="flex items-center gap-1">
              {/* File Upload */}
              {firstSelectedModelInfo && (
                <FileUploadButton
                  modelInfo={firstSelectedModelInfo}
                  onFileSelected={async (file: File | null) => {
                    if (!file) return;
                    const fileType = file.type;
                    const isImage = fileType.startsWith('image/');
                    const isAudio = fileType.startsWith('audio/');
                    const isVideo = fileType.startsWith('video/');
                    const isPDF = fileType === 'application/pdf';
                    
                    if (!isImage && !isAudio && !isVideo && !isPDF) {
                      toast({
                        title: "Unsupported file type",
                        description: "Please upload an image, audio, video, or PDF file.",
                        variant: "destructive",
                      });
                      return;
                    }
                    
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64data: any = reader.result;
                      if (typeof base64data === 'string') {
                        setFileAttached(prev => [...prev, {
                          type: getContentType(fileType),
                          data: base64data,
                          filename: file.name
                        }]);
                      }
                    };
                    reader.readAsDataURL(file);
                  }}
                  toast={toast}
                />
              )}
            </div>
            
            {/* Right side - Model count */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-muted-foreground hidden sm:inline truncate max-w-[100px]">
                {selectedModels.length} model{selectedModels.length > 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
